<template>
  <div tabindex="-1" @click="handleClick">
    <!-- ------------------------------------POP-UP------------------------------------------ -->
    <!-- 
    This pop-up window aims to provide users with the ability to customize the name of the exported file. 
    The structure of the window will consist of a list of checkboxes, 
    with each checkbox corresponding to specific pieces of information that users can include in the file name. 
    Additionally, users will be able to preview a dynamically updated filename based on their checkbox selections.

    Furthermore, this feature should enable users to directly edit the filename, 
    should they choose to do so. If the filename is edited, the checkboxes should be automatically disabled.

    The envisioned appearance of this feature is illustrated below:
    FILENAME(dynamically rendered, can be directly edited)
    [] OPTION 1       [] OPTION 2     [] OPTION 3
    -->

    <v-dialog v-model="popUpOpen" max-width="60%" persistent>
      <v-card class="rounded-lg">
        <v-card-title color="primary" class="primary primary-title pa-2">
          <v-icon color="white darken-3">mdi-download</v-icon>
          <span class="white--text title pl-2"> Export </span>
        </v-card-title>

        <v-card-text>
          <v-text-field
            ref="vtf"
            v-model="displayedFileNameStr"
            :rules="[() => !!displayedFileNameStr || 'Filename is required.']"
            label="Filename"
            text
            clearable
            class="mt-4"
          >
          </v-text-field>

          <v-radio-group v-model="selectedExportType" label="Format:" row>
            <v-radio
              v-for="(gt, i) in graphTypes"
              :key="'ff-' + gt['key'] + '-' + i"
              :label="gt['label']"
              :value="gt['key']"
            ></v-radio>
          </v-radio-group>

          <div v-if="showOptions" class="text-h6" style="margin-bottom: 10px;">
            Options
          </div>
          <div
            v-if="showOptions"
            ref="checkboxCont"
            class="checkboxContainer"
            style="display:flex; justify-content: center; gap:1em; flex-wrap: wrap;"
          >
            <v-checkbox
              v-for="(value, key) in processedFileName"
              :key="'oc-' + key"
              v-model="selectedCheckboxes"
              :label="replaceAll(key, '_', ' ')"
              :value="key"
              dense
              style="margin-bottom: 10px;"
            />
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <!-- export button -->
          <v-btn text color="red lighten-2" @click="popUpOpen = false">
            <v-icon left>close</v-icon> Cancel
          </v-btn>
          <v-btn color="primary" @click="exportHandler()">Export</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ------------------------------------BUTTONS------------------------------------------ -->
    <div class="cont">
      <!-- EXPORT PDF -->
      <v-tooltip top color="black">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            class="primary mx-2"
            x-small
            v-bind="attrs"
            @click="showDialog('pdf')"
            v-on="on"
            >Export PDF</v-btn
          >
        </template>
        <span>Preserve the original dimensions of the document</span>
      </v-tooltip>

      <!-- EXPORT ADJUSTED PDF -->
      <v-tooltip top color="black">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            class="primary mx-2"
            x-small
            v-bind="attrs"
            @click="showDialog('adjustedPdf')"
            v-on="on"
            >Export Adjusted PDF</v-btn
          >
        </template>
        <span
          >Optimal formatting for letter-sized paper, ideal for printing</span
        >
      </v-tooltip>

      <!-- EXPORT SVG -->
      <v-tooltip top color="black">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            class="primary mx-2"
            x-small
            v-bind="attrs"
            @click="showDialog('svg')"
            v-on="on"
            >Export SVG</v-btn
          >
        </template>
        <span
          >A format suitable for developers, offering scalable vector
          graphics</span
        >
      </v-tooltip>
      <div id="hiddenElement" style=""></div>
    </div>
    <!-- ------------------------------------------------------------------------------ -->
  </div>
</template>

<script>
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default {
  props: {
    // these props depend on where this component lives
    // ---------------------------------------
    // No prop:
    // <export-interface/>
    // <export-content-component>
    // ---------------------------------------
    // secLevelParent prop:
    // <parent-component>
    //     <export-interface/>
    // <parent-component/>
    // <export-content-component>
    // ---------------------------------------
    // thirdLevelParent prop:
    // <parent-component>
    //     <parent-component>
    //         <export-interface/>
    //     <parent-component/>
    // <parent-component/>
    // <export-content-component>
    // ---------------------------------------
    secLevelParent: {
      type: Boolean,
      required: false,
      default: false,
    },
    thirdLevelParent: {
      type: Boolean,
      required: false,
      default: false,
    },
    // If true, there is more than one element with class "export-content" in the parent component
    multiSelect: {
      type: Boolean,
      required: false,
      default: false,
    },
    // analysis that the exported plot comes from
    analysis: {
      type: Object,
      required: false,
      default: null,
    },
    // (single) variable that the exported plot displays
    variable: {
      type: Object,
      required: false,
      default: null,
    },
    // type of thing being exported e.g., boxplot, longitudinal graph
    exportType: {
      type: String,
      required: false,
      default: 'graph',
    },
    // whether to display "Options" checkboxes
    showOptions: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      content: null,
      contentSource: null,
      canvas: null,
      popUpOpen: false,
      keys: [
        'Title',
        'Study_Dataset_Id',
        'Analysis_Id',
        'Selected_Variable',
        'Graph_Type',
      ],
      graphTypes: [
        {
          key: 'pdf',
          label: 'PDF',
          description: 'Preserve the original dimensions of the document',
        },
        {
          key: 'adjustedPdf',
          label: 'Adjusted PDF',
          description:
            'Optimal formatting for letter-sized paper, ideal for printing',
        },
        {
          key: 'svg',
          label: 'SVG',
          description:
            'A format suitable for developers, offering scalable vector graphics.',
        },
      ],
      processedFileName: {},
      displayedFileNameStr: null,
      selectedExportType: null,
      selectedCheckboxes: [],
    };
  },
  watch: {
    selectedCheckboxes(sc) {
      let scd = {};
      sc.forEach(k => {
        scd[k] = 1;
      });
      this.displayedFileNameStr =
        this.keys
          .filter(k => k in scd)
          .map(k => this.processedFileName[k])
          .join('_')
          .replace(/\s/, '_') + this.getFilenameSuffix();
    },
    selectedExportType() {
      this.updateFilenameAndCheckboxes();
    },
    variable() {
      this.updateFilenameAndCheckboxes();
    },
  },

  mounted() {
    this.updateFilenameAndCheckboxes();
  },

  methods: {
    updateFilenameAndCheckboxes() {
      let checkboxes = [];
      this.processedFileName['Title'] = 'podvis_' + window.location.hostname;
      this.processedFileName['Study_Dataset_Id'] =
        'd' + this.analysis.collection.id;
      this.processedFileName['Analysis_Id'] = 'a' + this.analysis.index;
      if (this.variable != null) {
        this.processedFileName[
          'Selected_Variable'
        ] = this.variable.abbreviation;
        checkboxes.push('Selected_Variable');
      }
      this.processedFileName['Graph_Type'] = this.exportType;
      ['Title', 'Study_Dataset_Id', 'Analysis_Id', 'Graph_Type'].forEach(k =>
        checkboxes.push(k)
      );
      this.selectedCheckboxes = checkboxes;
    },

    getFilenameSuffix() {
      return this.selectedExportType == 'svg' ? '.svg' : '.pdf';
    },

    // Ensure the filename ends with ".pdf"
    getFilenameWithSuffix() {
      let fname = this.displayedFileNameStr;
      // strip off existing suffix
      let m = fname.match(/^(.*)(\.svg|\.pdf)$/);
      if (m) {
        fname = m[1];
      }

      return fname + this.getFilenameSuffix();
    },

    // Triggered when the export button in pop-up window is clicked
    // select the correct export function based on selectedExportType
    exportHandler() {
      // close the pop-up window
      // this.$refs.popup.style.display = "none";
      this.popUpOpen = false;

      if (this.selectedExportType === 'pdf') {
        setTimeout(() => {
          this.exportFullGraphPdf();
        }, 100);
      } else if (this.selectedExportType === 'adjustedPdf') {
        setTimeout(() => {
          this.exportAdjustedGraphPdf();
        }, 100);
      } else if (this.selectedExportType === 'svg') {
        setTimeout(() => {
          this.exportToSvg();
        }, 100);
      }
    },

    replaceAll(str, find, replace) {
      return str.replace(new RegExp(find, 'g'), replace);
    },

    // -----------------------------------Pop-Up Window-------------------------------------------------

    showDialog(graphType) {
      this.popUpOpen = true;
      this.selectedExportType = graphType;
    },
    closePopUp() {
      this.popUpOpen = false;
    },
    handleClick(event) {
      // Access the target element that triggered the event
      // console.log("Target element:", event.target);

      //if event.target has the class name of "popup", then popUpTrigger = false
      if (event.target.className === 'popup') {
        this.popUpOpen = false;
      }
    },

    // -----------------------------------Actual Export Algorithm-------------------------------------------------
    // when any btn is clicked, the export funtions will first call this helper function to find the correct content
    async selectContent() {
      // html2canvas callback to remove box-shadows from v-sheets, as they are not supported by html2canvas
      let onCloneFn = function(doc) {
        doc.querySelectorAll('.v-sheet').forEach(vs => {
          vs.style.boxShadow = 'none';
        });
      };

      if (this.multiSelect) {
        if (!this.secLevelParent && !this.thirdLevelParent) {
          this.content = this.$parent.$el.querySelectorAll('.export-content');
        } else if (this.secLevelParent && !this.thirdLevelParent) {
          this.content = this.$parent.$parent.$el.querySelectorAll(
            '.export-content'
          );
        } else if (!this.secLevelParent && this.thirdLevelParent) {
          this.content = this.$parent.$parent.$parent.$el.querySelectorAll(
            '.export-content'
          );
        } else if (this.secLevelParent && this.thirdLevelParent) {
          this.content = null;
        }

        // generate the canvas here
        // if there is more than one element with class "export-content" in the parent component, then we need to combine the canvases
        if (this.content.length > 1) {
          // Create a canvas with the full content
          const canvas2 = await html2canvas(this.content[1], {
            scrollX: -window.scrollX,
            scrollY: -window.scrollY,
            windowWidth: document.documentElement.offsetWidth,
            windowHeight: document.documentElement.offsetHeight,
            onclone: onCloneFn,
            logging: false,
          });

          const canvas1 = await html2canvas(this.content[0], {
            scrollX: -window.scrollX,
            scrollY: -window.scrollY,
            width: canvas2.width, // this makes sure the title has the same width as the content, otherwise a box will be drawn around the empty space
            windowWidth: document.documentElement.offsetWidth,
            windowHeight: document.documentElement.offsetHeight,
            onclone: onCloneFn,
            logging: false,
          });

          // combine the two canvases into one using drawImage
          let temp = document.createElement('canvas');
          temp.width = canvas2.width;
          temp.height = canvas1.height + canvas2.height;
          const ctx = temp.getContext('2d');
          ctx.drawImage(canvas1, 0, 0);
          ctx.drawImage(canvas2, 0, canvas1.height);
          this.canvas = temp;
        }
        // if there is only one element with class "export-content" in the parent component, then we can just use html2canvas
        else {
          this.canvas = await html2canvas(this.content[0], {
            onclone: onCloneFn,
            logging: false,
          });
        }
      } else {
        if (!this.secLevelParent && !this.thirdLevelParent) {
          this.content = this.$parent.$el.querySelector('.export-content');
        } else if (this.secLevelParent && !this.thirdLevelParent) {
          this.content = this.$parent.$parent.$el.querySelector(
            '.export-content'
          );
          // console.log(this.content)
        } else if (!this.secLevelParent && this.thirdLevelParent) {
          this.content = this.$parent.$parent.$parent.$el.querySelector(
            '.export-content'
          );
        } else if (this.secLevelParent && this.thirdLevelParent) {
          this.content = null;
        }

        this.canvas = await html2canvas(this.content, {
          onclone: onCloneFn,
          logging: false,
        });
      }
    },

    async exportFullGraphPdf() {
      // this will set this.content to a list of all elements with class "export-content"
      await this.selectContent();

      // Convert the canvas to an image
      const imgData = this.canvas.toDataURL('image/png');

      let pdf = null;
      // if width > height, then orientation *must* be set to landscape:
      if (this.canvas.width > this.canvas.height) {
        pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'px',
          format: [this.canvas.width, this.canvas.height],
        });
      } else {
        pdf = new jsPDF({
          unit: 'px',
          format: [this.canvas.width, this.canvas.height],
        });
      }

      // Calculate the aspect ratio for scaling the image
      const imgWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (this.canvas.height * imgWidth) / this.canvas.width;

      //console.log("canvas.width: ", canvas.width)
      //console.log("canvas.height: ",canvas.height)
      //console.log("imgHeight: ",imgHeight)
      //console.log("pdfinternalwidth: ",pdf.internal.pageSize.getWidth());
      //console.log("pdfinternalheight: ",pdf.internal.pageSize.getHeight());
      // Calculate PDF page size

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(this.getFilenameWithSuffix());
      document.getElementById('hiddenElement').style.display = 'none';
    },

    async exportAdjustedGraphPdf() {
      await this.selectContent();

      // Convert the canvas to an image
      const imgData = this.canvas.toDataURL('image/png');

      // Calculate PDF page size
      const pdf = new jsPDF('p', 'pt', 'a4');

      // if both width and height are smaller than a4 size, then use no adjustment
      if (this.canvas.width < 595 && this.canvas.height < 842) {
        // Add the image to the PDF
        pdf.addImage(
          imgData,
          'PNG',
          0,
          0,
          this.canvas.width,
          this.canvas.height
        );
        pdf.save(this.getFilenameWithSuffix());
      }
      // Calculate the aspect ratio for scaling the image
      else {
        let widthRatio = this.canvas.width / 595;
        let heightRatio = this.canvas.height / 842;
        let ratio = Math.max(widthRatio, heightRatio);
        let newWidth = this.canvas.width / ratio;
        let newHeight = this.canvas.height / ratio;
        // console.log("newWidth: ",newWidth)
        // console.log("newHeight: ",newHeight)
        // Add the image to the PDF
        pdf.addImage(imgData, 'PNG', 0, 0, newWidth, newHeight);
        pdf.save(this.getFilenameWithSuffix());
      }
    },

    async exportToSvg() {
      await this.selectContent();
      // Create a canvas with the full content
      const svgMarkup = this.canvasToSvg(this.canvas);
      const blob = new Blob([svgMarkup], { type: 'image/svg+xml' });

      // Create a download link and trigger click
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = this.getFilenameWithSuffix();
      link.click();
    },

    canvasToSvg(canvas) {
      const dataUrl = canvas.toDataURL('image/png');
      const svgMarkup = `
        <svg xmlns="http://www.w3.org/2000/svg" width="${
          canvas.width
        }" height="${canvas.height}">
        <image href="${dataUrl}" width="${canvas.width}" height="${
        canvas.height
      }" />
        </svg>
    `;

      return svgMarkup;
    },
  },
};
</script>

<style scoped>
.cont {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 350px;
}

.popup {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99;
  background-color: rgba(0, 0, 0, 0.2);

  display: flex;
  align-items: center;
  justify-content: center;

  .popup-inner {
    background: #fff;
    padding: 32px;
    width: 60%;
  }
}

.container1 {
  position: relative;
  /* border: 1px solid #ccc; */
  padding: 10px;
  /* margin: 20px; */
}

.title {
  font-size: 18px;
  padding: 10px;
  /* background-color: #f0f0f0; */
  margin: 0;
  border-radius: 5px 5px 0 0;
}
</style>
